<script setup lang="ts">
import Modal from "~/components/ui/Modal.vue";
import KenticoHeader from "./KenticoHeader.vue";
import KenticoAccordionTab from "./AccordionTab.vue";
import { contentToMap } from "~/utils/kentico/ContentParser";
import Accordion from "~/components/ui/Accordion.vue";
import { useSettings } from "~/store/settings";
import { INode } from "~/types/kentico/parser";
import { sitemapMatcher } from "~/utils/sitemaps/sitemapMatcher";
import SuperBoostEvents from "~/components/layout/Sport/SuperBoosts/SuperBoostEvents.vue";
import t from "~/utils/translate";
const settings = useSettings();

const modal = ref<string | null>(null);

const header = computed(() => {
	try {
		const routeFilterOptions = calculateSitemapPath();
		const filteredData = sitemapMatcher({ sitemap: settings.getSitemap, routeFilterOptions });
		if (filteredData?.A) {
			return filteredData?.A;
		}
		if (filteredData?.PN) {
			return filteredData?.PN;
		}
	} catch (e) {
		return undefined;
	}
});

export interface IExpandingContentProps {
	content?: string | null;
	node?: INode | null;
	scripts?: string[] | null;
	styles?: string[] | null;
	noHeader?: boolean;
}

const props = withDefaults(defineProps<IExpandingContentProps>(), {
	content: null,
	scripts: null,
	styles: null,
	node: null,
});

const obj = await contentToMap(props.node, [
	"page-heading",
	"page-body",
	"accordion-item",
	"super-boosts",
	"accordion-header",
	"accordion-content",
	"accordion-icon",
	"modal-body",
]);

onUnmounted(() => {
	document.querySelectorAll("[data-kentico-styles]").forEach((element) => {
		element.remove();
	});
	document.querySelectorAll("[data-kentico-script]").forEach((element) => {
		element.remove();
	});
});

useHead({
	script: props.scripts ? props.scripts?.map((x) => x) : [],
	style: props.styles ? props.styles?.map((x) => x) : [],
});

const setActive = () => {
	modal.value = null;
};
declare global {
	interface Window {
		openContentModal: (title: string) => void;
	}
}

onMounted(() => {
	window.openContentModal = (title: string) => {
		modal.value = title;
	};
});
</script>

<template>
	<Modal :active="!!modal" @set-active="setActive()" :title="modal ?? ''" :content-padding="false">
		<div class="m-4" v-html="obj?.get('modal-body')" />
	</Modal>
	<KenticoHeader v-if="!noHeader" :header="header ? t(header) : ''" />
	<div class="mt-3 text-xs" v-if="!obj?.get('page-body')" v-html="content"></div>
	<div class="content-container" v-if="obj?.get('page-body')">
		<div v-html="obj?.get('page-body')" class="m-3 leading-5 text-left text-dark-700" />

		<SuperBoostEvents v-if="obj?.has('super-boosts')" />

		<Accordion
			v-if="obj?.get('accordion-item')"
			:tabs="obj?.get('accordion-item')"
			class="overflow-hidden rounded p-[1px] m-3"
			:headerWrapperClass="'w-full gap-1 rounded-t-lg dark:bg-dark-700 text-dark-300  relative'"
			:activeIndex="-1"
		>
			<KenticoAccordionTab v-for="i in obj?.get('accordion-item')" :key="i" :header="i.get('accordion-header')" :content="i.get('accordion-content')">
			</KenticoAccordionTab>
		</Accordion>
	</div>
</template>
